/**
 * VistoeraTASK-00523 - Canvas picker's view of the canonical folder tree.
 *
 * Reads the SAME `/images/collections` API as the Image Asset Center, so a
 * folder created on either side is the same row. Team mode comes from the
 * server (>= 2 active members of THIS Workspace) - the picker never decides it.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createImageFolder,
  deleteImageFolder,
  listImageFolders,
  renameImageFolder,
  type ImageFoldersResponse,
  type SaasApiError,
} from '../../api'
import { isCancelledError } from '../../workspace/imageAssetLoadErrors'

/** Last known team-mode answer per Workspace, so re-opening never flashes the legacy layout. */
const teamEnabledCache = new Map<string, boolean>()

export type FolderLoadStatus = 'idle' | 'loading' | 'ready' | 'error'

export function useImagePickerFolders(workspaceId: string, open: boolean) {
  const [data, setData] = useState<ImageFoldersResponse | null>(null)
  const [status, setStatus] = useState<FolderLoadStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const seqRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)

  const reload = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!workspaceId) return
      const seq = ++seqRef.current
      abortRef.current?.abort()
      const abort = new AbortController()
      abortRef.current = abort
      if (!opts?.silent) setStatus('loading')
      try {
        const { data: body } = await listImageFolders(workspaceId, abort.signal)
        if (seq !== seqRef.current) return
        teamEnabledCache.set(workspaceId, Boolean(body.team?.enabled))
        setData(body)
        setStatus('ready')
        setErrorMessage(null)
      } catch (err) {
        if (seq !== seqRef.current) return
        if (isCancelledError(err)) return
        // Keep whatever tree is already on screen; only flag the failure.
        setStatus('error')
        setErrorMessage((err as SaasApiError).message || null)
      }
    },
    [workspaceId],
  )

  useEffect(() => {
    if (!open) {
      seqRef.current += 1
      abortRef.current?.abort()
      return
    }
    setData(null)
    void reload()
  }, [open, reload])

  const createFolder = useCallback(
    async (name: string, parentId: string | null) => {
      const { data: row } = await createImageFolder(workspaceId, name, parentId)
      await reload({ silent: true })
      return row
    },
    [workspaceId, reload],
  )
  const renameFolder = useCallback(
    async (folderId: string, name: string) => {
      await renameImageFolder(workspaceId, folderId, name)
      await reload({ silent: true })
    },
    [workspaceId, reload],
  )
  const deleteFolder = useCallback(
    async (folderId: string) => {
      await deleteImageFolder(workspaceId, folderId)
      await reload({ silent: true })
    },
    [workspaceId, reload],
  )

  const teamEnabled = data ? Boolean(data.team?.enabled) : (teamEnabledCache.get(workspaceId) ?? false)

  return {
    data,
    status,
    errorMessage,
    teamEnabled,
    folders: data?.collections ?? [],
    canManage: Boolean(data?.can_manage),
    reload,
    createFolder,
    renameFolder,
    deleteFolder,
  }
}
